import { useState } from 'react'
import { ArrowRight, CalendarClock, History as HistoryIcon, ScrollText } from 'lucide-react'
import { NOT_ENTITLED, get } from '../api'
import { date, dateTime, words } from '../format'
import { Button, Chip, Empty, Panel, Refusal } from '../ui'

// ======================================================================
// TRANSITIONS — THE SUBJECT-FACING TIMELINE
// ======================================================================
//
// GATED ON view_case, NOT view_case_audit, and that difference is the
// point of the endpoint. The subject does not hold view_case_audit and
// should not — that chain carries every access denial and break-glass
// grant. But the subject is precisely the person entitled to know that
// their hearing slipped by three days and why.
//
// So this list carries the dates and the reasons and nothing else: no
// actor ids, no addresses, no hashes, no audit detail. `reason` is shown
// verbatim, which is both the intent and a caution to whoever writes one.
//
// from_stage == to_stage means the row moved no stage — a pause, a
// resume, a target change or a closure. Those four cannot be told apart
// from the row alone; the target_before/target_after pair identifies a
// date change, and the rest are rendered as what they are: a recorded
// change with a reason.

export function Transitions({ caseId, page: initial }) {
  const { items, more, loading, error, setError, loadMore } = usePaged(
    caseId,
    '/transitions',
    initial,
  )

  return (
    <Panel eyebrow="What has happened" title="Case timeline">
      <Refusal error={error} onDismiss={() => setError(null)} />
      {items.length === 0 ? (
        <Empty icon={HistoryIcon} title="Nothing has changed yet">
          Stage moves, pauses and changes to the target date appear here, each
          with the reason it was given.
        </Empty>
      ) : (
        <ol className="space-y-0">
          {items.map((t, i) => {
            const movedStage = t.from_stage !== t.to_stage
            const movedTarget =
              t.target_before !== t.target_after &&
              (t.target_before || t.target_after)
            return (
              <li key={`${t.created_at}-${i}`} className="relative flex gap-3.5 pb-5">
                <div className="flex flex-col items-center">
                  <span
                    className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-accent"
                    style={{ boxShadow: '0 0 6px var(--accent-glow)' }}
                  />
                  {i < items.length - 1 && (
                    <span className="mt-1 w-px flex-1 bg-line" />
                  )}
                </div>

                <div className="min-w-0 flex-1 pt-0.5">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {movedStage ? (
                      <span className="flex items-center gap-1.5 font-head text-[12.5px] font-semibold">
                        {t.from_stage ? words(t.from_stage) : 'opened'}
                        <ArrowRight size={12} strokeWidth={2} className="text-faint" />
                        {words(t.to_stage)}
                      </span>
                    ) : (
                      <span className="font-head text-[12.5px] font-semibold">
                        {movedTarget ? 'Target date changed' : 'Recorded change'}
                      </span>
                    )}
                    <span className="text-[11.5px] text-faint">
                      {dateTime(t.created_at)}
                    </span>
                  </div>

                  {movedTarget && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <CalendarClock size={13} strokeWidth={1.8} className="text-amber" />
                      <span className="text-[12px]">
                        <span className="text-faint">
                          {t.target_before ? date(t.target_before) : 'no target'}
                        </span>
                        <ArrowRight
                          size={11}
                          strokeWidth={2}
                          className="mx-1.5 inline text-faint"
                        />
                        <span className="text-amber">
                          {t.target_after ? date(t.target_after) : 'no target'}
                        </span>
                      </span>
                    </div>
                  )}

                  {t.reason && (
                    <p className="measure mt-1.5 whitespace-pre-wrap text-[12.5px] leading-relaxed text-muted">
                      <span className="text-faint">Reason: </span>
                      {t.reason}
                    </p>
                  )}

                  {t.performed_by_label && (
                    <p className="mt-1 text-[11.5px] text-faint">
                      by {t.performed_by_label}
                    </p>
                  )}
                </div>
              </li>
            )
          })}
        </ol>
      )}
      {more && (
        <Button size="sm" busy={loading} onClick={loadMore}>
          Show earlier entries
        </Button>
      )}
    </Panel>
  )
}

// ======================================================================
// AUDIT
// ======================================================================
//
// FIVE COLUMNS, AND WHAT IS ABSENT MATTERS MORE THAN WHAT IS PRESENT.
// row_hash, prev_hash, detail and ip_address are not in the response at
// all — not fetched and dropped, never selected. The hashes are the
// chain, and publishing them to every reader hands out the material for
// reconstructing a forged one; `detail` is free-form JSON whose audience
// was never checked against the capability that opens this list.
//
// TWO ENDPOINTS, TWO AUDIENCES. /audit is the whole trail and needs
// view_case_audit — the roles accountable for how the case was handled.
// /audit/mine is filtered to the caller's own rows in SQL and is what
// the subject and the reporter get. A caller who holds only the second
// sees only their own actions, and this component is given whichever one
// the workspace was entitled to fetch.

export function Audit({ caseId, page: initial, scope }) {
  const path = scope === 'mine' ? '/audit/mine' : '/audit'
  const { items, more, loading, error, setError, loadMore } = usePaged(
    caseId,
    path,
    initial,
  )

  return (
    <Panel
      eyebrow={scope === 'mine' ? 'Your own actions' : 'Everything recorded'}
      title={scope === 'mine' ? 'Your activity on this case' : 'Audit trail'}
    >
      <Refusal error={error} onDismiss={() => setError(null)} />
      {items.length === 0 ? (
        <Empty icon={ScrollText} title="Nothing recorded yet" />
      ) : (
        <ul className="divide-y divide-line">
          {items.map((row) => (
            <li key={row.seq} className="flex items-baseline gap-3 py-2">
              <span className="mono w-10 shrink-0 text-right text-faint">{row.seq}</span>
              <div className="min-w-0 flex-1">
                <span className="text-[12.5px]">{words(row.event)}</span>
                {row.object_type && (
                  <span className="ml-2">
                    <Chip>{words(row.object_type)}</Chip>
                  </span>
                )}
              </div>
              {row.actor_label && (
                <span className="shrink-0 text-[11.5px] text-muted">
                  {row.actor_label}
                </span>
              )}
              <span className="shrink-0 text-[11.5px] text-faint">
                {dateTime(row.created_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {more && (
        <Button size="sm" className="mt-3" busy={loading} onClick={loadMore}>
          Show earlier entries
        </Button>
      )}
    </Panel>
  )
}

/** `has_more` rather than a total, because that is what the backend
 *  returns: a COUNT(*) over the chain on every page view is work nobody
 *  asked for, and the exact number of audit rows on a case is itself a
 *  small signal about how much has happened on it. */
function usePaged(caseId, suffix, initial) {
  const [items, setItems] = useState(initial.items)
  const [offset, setOffset] = useState(initial.items.length)
  const [more, setMore] = useState(initial.has_more)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const loadMore = async () => {
    setLoading(true)
    setError(null)
    try {
      const next = await get(`/${caseId}${suffix}?limit=50&offset=${offset}`)
      if (next === NOT_ENTITLED) {
        setMore(false)
        return
      }
      setItems((prev) => [...prev, ...next.items])
      setOffset((o) => o + next.items.length)
      setMore(next.has_more)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }

  return { items, more, loading, error, setError, loadMore }
}
